/**
 * Due-date presets (Arthur, 23 October, ruling 11): a due spec resolved against the agency's today (New York).
 * api/src/due-dates.ts is the source; web/src/dueDates.ts is a byte-for-byte copy, and a test in each suite refuses
 * any difference (shared-copies.test.ts). Pure: no imports, no Node or browser API, and no clock: today is passed in.
 *
 *   tomorrow       today + 1            2_days    today + 2
 *   1_week         today + 7            2_weeks   today + 14
 *   end_of_week    the first Friday on or after today
 *   after_weekend  the first Monday after today
 *   in_days n      today + n (1-365)
 *   in_business_days n   step from today, counting Monday to Friday only, until n are counted (1-260); no holidays
 *   date           that date, today or later
 */
export type DueSpec =
  | { preset: 'tomorrow' | '2_days' | 'end_of_week' | 'after_weekend' | '1_week' | '2_weeks' }
  | { in_days: number }
  | { in_business_days: number }
  | { date: string }
  | null;

/**
 * A refusal is a CODE from this closed set, never a sentence built here (A-#135, 26 October 2026): the API sends
 * DUE_WORDS[code], so nothing the request sent can reach a refusal, and the compiler refuses any other string.
 */
export type DueError = 'no_today' | 'not_a_choice' | 'days_range' | 'business_days_range' | 'date_unread' | 'past';
export type Resolved = { due: string | null } | { error: DueError };

/** The fixed sentence for each refusal (ruling 11). */
export const DUE_WORDS: Readonly<Record<DueError, string>> = {
  no_today: 'Today could not be read.',
  not_a_choice: 'That is not a due-date choice.',
  days_range: 'Say a number of days from 1 to 365.',
  business_days_range: 'Say a number of business days from 1 to 260.',
  date_unread: 'That date could not be read.',
  past: 'A task cannot be due in the past.',
};

const DATE_SHAPE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A calendar day as a UTC midnight, so adding days never meets a clock change. */
function toDay(s: string): Date | null {
  const m = DATE_SHAPE.exec(s);
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return d.getUTCFullYear() === Number(m[1]) && d.getUTCMonth() === Number(m[2]) - 1 && d.getUTCDate() === Number(m[3]) ? d : null;
}
function fromDay(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}
function plus(d: Date, n: number): Date {
  return new Date(d.getTime() + n * 86400000);
}
/** 0 Sunday .. 6 Saturday */
const weekday = (d: Date) => d.getUTCDay();

export function resolveDue(spec: DueSpec, today: string): Resolved {
  const t = toDay(today);
  if (!t) return { error: 'no_today' };
  if (spec === null || spec === undefined) return { due: null };
  if ('preset' in spec) {
    switch (spec.preset) {
      case 'tomorrow': return { due: fromDay(plus(t, 1)) };
      case '2_days': return { due: fromDay(plus(t, 2)) };
      case '1_week': return { due: fromDay(plus(t, 7)) };
      case '2_weeks': return { due: fromDay(plus(t, 14)) };
      case 'end_of_week': return { due: fromDay(plus(t, (5 - weekday(t) + 7) % 7)) };
      case 'after_weekend': return { due: fromDay(plus(t, ((1 - weekday(t) + 7) % 7) || 7)) };
      default: return { error: 'not_a_choice' };
    }
  }
  if ('in_days' in spec) {
    const n = spec.in_days;
    if (!Number.isInteger(n) || n < 1 || n > 365) return { error: 'days_range' };
    return { due: fromDay(plus(t, n)) };
  }
  if ('in_business_days' in spec) {
    const n = spec.in_business_days;
    if (!Number.isInteger(n) || n < 1 || n > 260) return { error: 'business_days_range' };
    let d = t;
    let counted = 0;
    while (counted < n) {
      d = plus(d, 1);
      const w = weekday(d);
      if (w !== 0 && w !== 6) counted += 1;
    }
    return { due: fromDay(d) };
  }
  if ('date' in spec) {
    const d = toDay(spec.date);
    if (!d) return { error: 'date_unread' };
    if (d < t) return { error: 'past' };
    return { due: spec.date };
  }
  return { error: 'not_a_choice' };
}
